import React, { memo, useCallback, useMemo, useRef } from "react";
import { Plus, MoreHorizontal } from "lucide-react";
import { useDrop } from "react-dnd";
import { useVirtualizer } from "@tanstack/react-virtual";
import { TicketCard } from "./TicketCard";
import { motion } from "motion/react";
import { StatusInterface } from "../../../../models/TicketModule/Status";
import { TicketInterface } from "../../../../models/TicketModule/Ticket";
import { useLanguageStore } from "../../../../store/useLanguageStore";

interface VirtualKanbanColumnProps {
  status: StatusInterface;
  tickets: TicketInterface[];
  count: number;
  onTicketMove: (ticketId: string, newStatusId: string) => void;
}

// keep your helper
function lightenColor(hex: string, percent: number) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.floor((num >> 16) + (255 - (num >> 16)) * percent));
  const g = Math.min(255, Math.floor(((num >> 8) & 0x00ff) + (255 - ((num >> 8) & 0x00ff)) * percent));
  const b = Math.min(255, Math.floor((num & 0x0000ff) + (255 - (num & 0x0000ff)) * percent));
  return `rgb(${r}, ${g}, ${b})`;
}

function VirtualKanbanColumnComponent({ status, tickets, count, onTicketMove }: VirtualKanbanColumnProps) {
  const { language } = useLanguageStore();

  const color = status.badgeColor || "#F3F4F6";
  const headerBg = useMemo(() => lightenColor(color, 0.85), [color]);
  const dropBorder = useMemo(() => lightenColor(color, 0.5), [color]);

  // scroll parent (virtualizer uses this)
  const parentRef = useRef<HTMLDivElement | null>(null);

  // stable drop handler
  const handleDrop = useCallback(
    (item: { id: string }) => onTicketMove(item.id, status.id || ""),
    [onTicketMove, status.id]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "TICKET",
      drop: handleDrop,
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [handleDrop]
  );

  // ✅ Virtualizer (supports variable heights)
  const rowVirtualizer = useVirtualizer({
    count: tickets.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 120, // rough average card height
    overscan: 8,
    // this is the key for expandable cards (variable height)
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const requestMeasure = useCallback(() => {
    // optional: rAF to batch multiple toggles/expands in same frame
    requestAnimationFrame(() => rowVirtualizer.measure());
  }, [rowVirtualizer]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          <div
            className="text-[11px] px-2 py-0.5 rounded-md font-medium uppercase tracking-wide"
            style={{ color, backgroundColor: headerBg }}
          >
            {status.title}
          </div>
          <div className="bg-gray-100 text-gray-500 text-[11px] px-2 py-0.5 rounded-md min-w-[24px] text-center font-medium">
            {count}
          </div>
        </div>

        <button className="text-gray-400 hover:text-gray-600 hover:bg-gray-50 p-1.5 rounded transition-all">
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {/* Scroll + Drop Area */}
      <div
        ref={(node) => {
          parentRef.current = node;
          drop(node);
        }}
        className="flex-1 relative overflow-y-auto p-3"
        style={{
          backgroundColor: isOver ? headerBg : "transparent",
          contain: "content",
        }}
      >
        {isOver && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-3 rounded-lg border-2 border-dashed bg-white/50 backdrop-blur-sm pointer-events-none z-10"
            style={{ borderColor: dropBorder }}
            transition={{ duration: 0.15 }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm font-medium bg-white px-4 py-2 rounded-lg shadow-sm" style={{ color }}>
                {language["DROP_HERE"]}
              </div>
            </div>
          </motion.div>
        )}

        {tickets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-16 text-gray-400"
          >
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Plus className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-sm">{language["NO_TICKETS"]}</p>
            <p className="text-xs text-gray-400 mt-1">{language["ADD_FIRST_TICKET"]}</p>
          </motion.div>
        ) : (
          // The “virtual canvas”
          <div style={{ height: totalSize, position: "relative" }}>
            {virtualItems.map((v) => {
              const ticket = tickets[v.index];

              return (
                <div
                  key={ticket.id}
                  ref={rowVirtualizer.measureElement}
                  data-index={v.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${v.start}px)`,
                    willChange: "transform",
                  }}
                >
                  {/* Keep animations LIGHT in virtual lists */}
                  <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.12 }}>
                    <TicketCard ticket={ticket} onHeightChange={requestMeasure} />
                  </motion.div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export const VirtualKanbanColumn = memo(VirtualKanbanColumnComponent, (prev, next) => {
  if (prev.status.id !== next.status.id) return false;
  if (prev.count !== next.count) return false;
  if (prev.tickets.length !== next.tickets.length) return false;

  // id/statusId check only (fast)
  for (let i = 0; i < prev.tickets.length; i++) {
    const a = prev.tickets[i];
    const b = next.tickets[i];
    if (a.id !== b.id) return false;
    if (a.statusId !== b.statusId) return false;
  }
  return true;
});
